import api from './api';

// Helper function to get the token from local storage
const getAuthToken = () => localStorage.getItem('token');

// Get Meetings List with Pagination and Title Filtering
export const getMeetings = async (page = 1, pageSize = 25, title = '') => {
  try {
    const response = await api.get("/meetings/", {
      params: { page, pageSize, title },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    // Map _id to id for each meeting
    const meetings = response.data.meetings.map((meeting: any) => ({
      ...meeting,
      id: meeting._id,
    }));

    return { ...response.data, meetings };
  } catch (error) {
    console.error("Error fetching meetings:", error);
    throw error;
  }
};

// Create a New Meeting
export const createMeeting = async (title: string) => {
  try {
    const response = await api.post(
      "/meetings/create",
      { title },
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    // Map _id to id for the newly created meeting
    const meeting = { ...response.data.meeting, id: response.data.meeting._id };

    return { ...response.data, meeting };
  } catch (error) {
    console.error("Error creating meeting:", error);
    throw error;
  }
};

// Delete a Single Meeting
export const deleteMeeting = async (id: string) => {
  try {
    const response = await api.delete(`/meetings/${id}/delete`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting meeting:", error);
    throw error;
  }
};

// Duplicate a Single Meeting
export const duplicateMeeting = async (id: string, options: {
  title: string;
  date: Date;
  includeShared: boolean;
  agendaOption: 'all' | 'incomplete';
  includeNotes: boolean;
  actionsOption: 'none' | 'all' | 'move-incomplete';  // Updated this line
}) => {
  try {
    const response = await api.post(
      `/meetings/${id}/duplicate`,
      options,
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    // Map _id to id to maintain consistency with other functions
    const meeting = { ...response.data.meeting, id: response.data.meeting._id };
    return { ...response.data, meeting };

  } catch (error) {
    console.error("Error duplicating meeting:", error);
    throw error;
  }
};

// Get Single Meeting
export const getMeeting = async (id: string) => {
  try {
    const response = await api.get(`/meetings/${id}`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    // Map _id to id for the fetched meeting
    const meeting = { ...response.data.meeting, id: response.data.meeting._id };

    console.log("Meeting loaded:", meeting);

    return { ...response.data, meeting };
  } catch (error) {
    console.error("Error fetching meeting:", error);
    throw error;
  }
};

// Update Meeting Info (title and date)
export const updateMeetingInfo = async (id: string, updates: { title?: string; date?: string }) => {
  try {
    const response = await api.put(`/meetings/${id}/update-info`, updates, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error updating meeting info:", error);
    throw error;
  }
};

//
// Shared with users
//

// Add Shared User
export const addSharedUser = async (id: string, email: string) => {
  try {
    const response = await api.put(
      `/meetings/${id}/add-shared-user`,
      { email },
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error adding shared user:", error);
    throw error;
  }
};

// Remove Shared User
export const removeSharedUser = async (id: string, email: string) => {
  try {
    const response = await api.put(
      `/meetings/${id}/remove-shared-user`,
      { email },
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error removing shared user:", error);
    throw error;
  }
};

//
// Agenda Items
//

// Add Agenda Item
export const addAgendaItem = async (id: string, agendaItem: { text: string; completed: boolean, order?: number }) => {
  try {
    const response = await api.put(
      `/meetings/${id}/add-agenda-item`,
      agendaItem, // Send agendaItem DIRECTLY
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding agenda item:", error);
    throw error;
  }
};

// Update Agenda Item
export const updateAgendaItem = async (
  id: string,
  agendaItemId: string,
  updatedAgendaItem: { text: string; completed: boolean }
) => {
  try {
    const response = await api.put(
      `/meetings/${id}/update-agenda-item/${agendaItemId}`,
      updatedAgendaItem,
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating agenda item:", error);
    throw error;
  }
};

// Update Agenda Order
export const updateAgendaOrder = async (id: string, newAgenda: { text: string; completed: boolean; order: number }[]) => {
  try {
    const response = await api.put(
      `/meetings/${id}/update-agenda-order`,
      newAgenda, // Send the array directly
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating agenda order:", error);
    throw error;
  }
};

// Delete Agenda Item
export const deleteAgendaItem = async (id: string, agendaItemId: string) => {
  try {
    const response = await api.delete(
      `/meetings/${id}/delete-agenda-item/${agendaItemId}`,
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error deleting agenda item:", error);
    throw error;
  }
};

//
// Meeting Notes
//

// Update Meeting Notes
export const updateMeetingNotes = async (id: string, notes: string) => {
  // create a console log with curret time stamp mm:ss so we can see when api called
  console.log("updateMeetingNotes api called at: ", new Date().getMinutes(), ":", new Date().getSeconds());
  try {
    const response = await api.put(
      `/meetings/${id}/update-notes`,
      { notes },
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating notes:", error);
    throw error;
  }
};

//
// Action Items
//

// Add Action Item
export const addActionItem = async (id: string, actionItem: { text: string; completed: boolean; hashtags?: string[] }) => {
  try {
    const response = await api.put(
      `/meetings/${id}/add-action-item`,
      actionItem, // Send actionItem DIRECTLY
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding action item:", error);
    throw error;
  }
};

// Update Action Item
export const updateActionItem = async (
  id: string,
  actionItemId: string,
  updatedActionItem: { text: string; completed: boolean; hashtags?: string[] }
) => {
  try {
    const response = await api.put(
      `/meetings/${id}/update-action-item/${actionItemId}`,
      updatedActionItem,
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating action item:", error);
    throw error;
  }
};

// Update Action Order (assuming order property exists in your action object)
export const updateActionOrder = async (id: string, newActions: { text: string; completed: boolean; order: number }[]) => {
  try {
    const response = await api.put(
      `/meetings/${id}/update-action-order`,
      newActions, // Send the array directly
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating action order:", error);
    throw error;
  }
};

// Delete Action Item
export const deleteActionItem = async (id: string, actionItemId: string) => {
  try {
    const response = await api.delete(
      `/meetings/${id}/delete-action-item/${actionItemId}`,
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error deleting action item:", error);
    throw error;
  }
};

//
// Previous Actions for same meeting title and owner
//
export const getPreviousActions = async (id: string, showCompleted = false) => {
  const apiUrl = `/meetings/${id}/previous-actions`;
  console.log('getPreviousActions calling URL:', apiUrl);
  try {
    const response = await api.get(`/meetings/${id}/previous-actions`, {
      params: { showCompleted },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    // Map _id to id for each meeting and its actions
    const meetings = response.data.meetings.map((meeting: any) => ({
      ...meeting,
      id: meeting._id,
      actions: meeting.actions.map((action: any) => ({
        ...action,
        id: action._id
      }))
    }));

    return { ...response.data, meetings };
  } catch (error) {
    console.error("Error fetching previous actions:", error);
    throw error;
  }
};